import { number, result } from './birthCalculations';
export interface GrowthRow {
  sex: 'MALE' | 'FEMALE';
  metric: 'weight' | 'length' | 'head_circumference';
  age_basis: 'POSTNATAL_DAYS' | 'GESTATIONAL_DAYS';
  age_days: number;
  unit: 'g' | 'cm';
  l: number;
  m: number;
  s: number;
}
export interface NewbornGrowthReference {
  id: string;
  provider: 'WHO' | 'FENTON' | 'CUSTOM';
  version: string;
  citation: string;
  clinically_reviewed: boolean;
  enabled: boolean;
  rows: GrowthRow[];
}
export interface NewbornGrowthConfiguration {
  references: NewbornGrowthReference[];
}
export type NewbornGrowthInput = Omit<GrowthRow, 'l' | 'm' | 's'> & { value: number };
const object = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const key = (row: Pick<GrowthRow, 'sex' | 'metric' | 'age_basis' | 'age_days'>) =>
  [row.sex, row.metric, row.age_basis, row.age_days].join(':');
export function validateNewbornGrowthConfiguration(value: unknown): string[] {
  if (
    !object(value) ||
    Object.keys(value).join(',') !== 'references' ||
    !Array.isArray(value.references) ||
    value.references.length > 20
  )
    return ['A bounded growth references list is required.'];
  const errors: string[] = [],
    ids = new Set<string>();
  let count = 0;
  for (const r of value.references) {
    if (
      !object(r) ||
      Object.keys(r).sort().join(',') !== 'citation,clinically_reviewed,enabled,id,provider,rows,version'
    ) {
      errors.push('Unsupported growth reference fields.');
      continue;
    }
    if (
      ['id', 'version', 'citation'].some(
        (k) => typeof r[k] !== 'string' || !(r[k] as string).trim() || (r[k] as string).length > 2000,
      ) ||
      !['WHO', 'FENTON', 'CUSTOM'].includes(String(r.provider)) ||
      typeof r.enabled !== 'boolean' ||
      typeof r.clinically_reviewed !== 'boolean' ||
      (r.enabled && !r.clinically_reviewed) ||
      ids.has(String(r.id))
    )
      errors.push('Unique source/version and explicit clinical review required.');
    ids.add(String(r.id));
    if (!Array.isArray(r.rows) || r.rows.length > 10000 || (r.enabled && !r.rows.length)) {
      errors.push('Enabled reference requires bounded source data rows.');
      continue;
    }
    count += r.rows.length;
    const rows = new Set<string>();
    for (const row of r.rows) {
      if (!object(row) || Object.keys(row).sort().join(',') !== 'age_basis,age_days,l,m,metric,s,sex,unit') {
        errors.push('Unsupported growth row fields.');
        continue;
      }
      if (
        !['MALE', 'FEMALE'].includes(String(row.sex)) ||
        !['weight', 'length', 'head_circumference'].includes(String(row.metric)) ||
        !['POSTNATAL_DAYS', 'GESTATIONAL_DAYS'].includes(String(row.age_basis)) ||
        !number(row.age_days) ||
        !Number.isInteger(row.age_days) ||
        row.age_days < 0 ||
        row.age_days > 36500 ||
        row.unit !== (row.metric === 'weight' ? 'g' : 'cm') ||
        !number(row.l) ||
        Math.abs(row.l) > 1000 ||
        !number(row.m) ||
        row.m <= 0 ||
        row.m > 1e9 ||
        !number(row.s) ||
        row.s <= 0 ||
        row.s > 1000
      ) {
        errors.push('Invalid growth dimensions, canonical units or finite LMS parameters.');
        continue;
      }
      const id = key(row as unknown as GrowthRow);
      if (rows.has(id)) errors.push('Duplicate growth reference age/sex/metric.');
      rows.add(id);
    }
  }
  if (count > 50000) errors.push('Too many growth data rows.');
  return errors;
}
/** Approximate standard normal CDF; maximum absolute approximation error <8e-8. */
export function normalCdf(z: number): number {
  if (z === 0) return 0.5;
  const x = Math.abs(z),
    t = 1 / (1 + 0.2316419 * x),
    density = Math.exp((-x * x) / 2) / Math.sqrt(2 * Math.PI);
  const tail =
    density * t * (0.31938153 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))));
  return z > 0 ? 1 - tail : tail;
}
export function newbornGrowth(input: NewbornGrowthInput, reference: NewbornGrowthReference | undefined) {
  type Value = { z_score: number; percentile: number; reference_id: string; reference_version: string };
  const inputs = {
    measurement: input,
    reference_id: reference?.id ?? null,
    reference_version: reference?.version ?? null,
  };
  const invalid = (warning: string) => result<Value | null>(null, inputs, [warning]);
  if (
    !reference ||
    validateNewbornGrowthConfiguration({ references: [reference] }).length ||
    !reference.enabled ||
    !reference.clinically_reviewed
  )
    return invalid('Enabled reviewed source data required.');
  if (
    !number(input.value) ||
    input.value <= 0 ||
    !number(input.age_days) ||
    !Number.isInteger(input.age_days) ||
    input.unit !== (input.metric === 'weight' ? 'g' : 'cm')
  )
    return invalid('Positive canonical measurement and explicit whole-day age required.');
  const row = reference.rows.find((row) => key(row) === key(input) && row.unit === input.unit);
  if (!row) return invalid('No exact reviewed age/sex/metric row; no interpolation or sex inference.');
  const ratio = Math.log(input.value / row.m);
  let z = row.l === 0 ? ratio / row.s : Math.expm1(row.l * ratio) / (row.l * row.s);
  if (!number(z) || Math.abs(z) > 3 + 1e-10)
    return invalid('Outside supported LMS interval (−3 to +3); no tail extrapolation.');
  z = Math.max(-3, Math.min(3, z));
  return result<Value | null>(
    {
      z_score: Math.round(z * 1e6) / 1e6,
      percentile: Math.round(normalCdf(z) * 100 * 1e4) / 1e4,
      reference_id: reference.id,
      reference_version: reference.version,
    },
    { ...inputs, row },
    [
      'Source: ' + reference.citation,
      'Numerical reference comparison, not a diagnosis. Exact supplied age basis; no corrected-age inference.',
    ],
  );
}
